import { Box, Typography } from "@mui/material"
import type { FC } from "react"
import { useMemo } from "react"

import type { InventoryAdjustmentHistorySearchParams } from "../schemas"

import { FilterChip } from "@/components/common/Filters"
import type { Option } from "@/types/form-fields"
import { formatDate } from "@/utils/date"

interface InventoryAdjustmentHistoryFilterChipsProps {
  searchParams: InventoryAdjustmentHistorySearchParams
  adjustmentTypeOptions: Option<string>[]
  statusOptions: Option<string>[]
  reasonOptions: Option<string>[]
  warehouseOptions: Option<string>[]
  adjustedByOptions: Option<string>[]
  onRemove: (key: keyof InventoryAdjustmentHistorySearchParams, value: string) => void
}

export const InventoryAdjustmentHistoryFilterChips: FC<InventoryAdjustmentHistoryFilterChipsProps> = ({
  searchParams,
  adjustmentTypeOptions,
  statusOptions,
  reasonOptions,
  warehouseOptions,
  adjustedByOptions,
  onRemove,
}) => {
  const adjustmentTypes = searchParams.adjustmentTypes ?? []
  const statuses = searchParams.statuses ?? []
  const adjustmentReasons = searchParams.adjustmentReasons ?? []
  const warehouseAreaIds = searchParams.warehouseAreaIds ?? []
  const adjustedByIds = searchParams.adjustedByIds ?? []
  const { dateFrom, dateTo } = searchParams

  const adjustmentTypeLabelsMap = useMemo(
    () => new Map(adjustmentTypeOptions.map((option) => [option.value, option.label])),
    [adjustmentTypeOptions]
  )
  const statusLabelsMap = useMemo(
    () => new Map(statusOptions.map((option) => [option.value, option.label])),
    [statusOptions]
  )
  const reasonLabelsMap = useMemo(
    () => new Map(reasonOptions.map((option) => [option.value, option.label])),
    [reasonOptions]
  )
  const warehouseLabelsMap = useMemo(
    () => new Map(warehouseOptions.map((option) => [option.value, option.label])),
    [warehouseOptions]
  )
  const adjustedByLabelsMap = useMemo(
    () => new Map(adjustedByOptions.map((option) => [option.value, option.label])),
    [adjustedByOptions]
  )

  const hasActiveFilters =
    !!dateFrom ||
    !!dateTo ||
    adjustmentTypes.length > 0 ||
    statuses.length > 0 ||
    adjustmentReasons.length > 0 ||
    warehouseAreaIds.length > 0 ||
    adjustedByIds.length > 0

  if (!hasActiveFilters) return null

  return (
    <Box className='flex flex-wrap items-center gap-2' data-testid='adjustment-history.filter-chips'>
      <Typography variant='body1' className='text-gray-700'>
        Applied filters:
      </Typography>

      {dateFrom && (
        <FilterChip
          label={`Date From: ${formatDate(dateFrom)}`}
          onDelete={() => onRemove("dateFrom", "")}
          testId='adjustment-history.filter-chips.date-from'
        />
      )}

      {dateTo && (
        <FilterChip
          label={`Date To: ${formatDate(dateTo)}`}
          onDelete={() => onRemove("dateTo", "")}
          testId='adjustment-history.filter-chips.date-to'
        />
      )}

      {adjustmentTypes.map((value) => {
        const label = adjustmentTypeLabelsMap.get(value)
        if (!label) return null
        return (
          <FilterChip
            key={value}
            label={`Adjustment Type: ${label}`}
            onDelete={() => onRemove("adjustmentTypes", value)}
            testId={`adjustment-history.filter-chips.adjustment-type-${value}`}
          />
        )
      })}

      {statuses.map((value) => {
        const label = statusLabelsMap.get(value)
        if (!label) return null
        return (
          <FilterChip
            key={value}
            label={`Status: ${label}`}
            onDelete={() => onRemove("statuses", value)}
            testId={`adjustment-history.filter-chips.status-${value}`}
          />
        )
      })}

      {adjustmentReasons.map((value) => {
        const label = reasonLabelsMap.get(value)
        if (!label) return null
        return (
          <FilterChip
            key={value}
            label={`Reason: ${label}`}
            onDelete={() => onRemove("adjustmentReasons", value)}
            testId={`adjustment-history.filter-chips.reason-${value}`}
          />
        )
      })}

      {warehouseAreaIds.map((value) => {
        const label = warehouseLabelsMap.get(value)
        if (!label) return null
        return (
          <FilterChip
            key={value}
            label={`Warehouse: ${label}`}
            onDelete={() => onRemove("warehouseAreaIds", value)}
            testId={`adjustment-history.filter-chips.warehouse-${value}`}
          />
        )
      })}

      {adjustedByIds.map((value) => {
        const label = adjustedByLabelsMap.get(value)
        if (!label) return null
        return (
          <FilterChip
            key={value}
            label={`Adjusted By: ${label}`}
            onDelete={() => onRemove("adjustedByIds", value)}
            testId={`adjustment-history.filter-chips.adjusted-by-${value}`}
          />
        )
      })}
    </Box>
  )
}
